import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ChevronDown, FileText, GripVertical, Loader2, Send, ShieldCheck, Users, X } from 'lucide-react';
import { envoyerDiffusion, fetchDiffusions, type ModeleDiffusion } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';

const horodatage = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Écrire à un groupe.
 *
 * Le bureau écrivait à tout le monde depuis sa messagerie personnelle, en
 * recopiant des adresses à la main dans un champ « à ». Deux conséquences,
 * toutes deux ordinaires : le fichier d'adhérents se retrouvait en clair chez
 * chacun des destinataires, et personne ne savait plus si le message était
 * déjà parti.
 *
 * Les audiences séparent deux natures de messages qui n'ont rien à voir. Ce
 * qui naît de l'engagement pris — une convocation, un changement d'horaire —
 * se transmet sans permission à demander. La lettre d'information exige un
 * accord explicite, et ne va qu'à ceux qui l'ont donné : l'écran le dit à
 * chaque fois, parce que c'est au moment d'écrire qu'on l'oublie.
 */
export default function DiffusionPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);
  const [confirme, setConfirme] = useState(false);
  const [modelesOuverts, setModelesOuverts] = useState(false);
  // Les listes retenues, dans l'ordre où on les a déposées.
  const [choisies, setChoisies] = useState<string[]>(['bureau']);
  const [glisse, setGlisse] = useState<string | null>(null);
  const [survol, setSurvol] = useState(false);

  const ajouter = (code: string) => {
    setChoisies((v) => (v.includes(code) ? v : [...v, code]));
    setConfirme(false);
  };
  const retirer = (code: string) => {
    setChoisies((v) => v.filter((c) => c !== code));
    setConfirme(false);
  };

  const peut = aLeDroit('diffuser_messages');

  const { data, isPending } = useQuery({
    queryKey: ['diffusions'],
    queryFn: fetchDiffusions,
    enabled: peut,
  });

  const envoyer = useMutation({
    mutationFn: () => envoyerDiffusion({ audiences: choisies, sujet, corps }),
    onSuccess: (r: any) => {
      setNotice({ type: 'ok', texte: r.message });
      setSujet('');
      setCorps('');
      setConfirme(false);
      queryClient.invalidateQueries({ queryKey: ['diffusions'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  // Une borne haute, et non l'union exacte : le navigateur ne connaît pas les
  // adresses, et ne peut donc pas savoir qui figure dans deux listes. C'est le
  // serveur qui dédoublonne, et qui annonce le compte réel après l'envoi.
  const nombreMax = useMemo(
    () =>
      (data?.audiences ?? [])
        .filter((a) => choisies.includes(a.code))
        .reduce((s, a) => s + a.destinataires, 0),
    [data, choisies]
  );

  const conflitNewsletter = choisies.includes('newsletter') && choisies.length > 1;

  if (authLoading || (peut && isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peut) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Écrire à un groupe dépend de votre fonction au bureau : un administrateur peut
              l'ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const nombre = nombreMax;
  const vide = nombre === 0;
  const pret =
    sujet.trim() !== '' && corps.trim() !== '' && !vide && confirme && !conflitNewsletter;

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Send className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Écrire à un groupe
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Un message part en copie individuelle à chacun : personne ne voit l'adresse des autres.
          </p>
        </div>
      </div>

      {notice && (
        <div
          className={`card text-sm flex items-start gap-3 ${
            notice.type === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1">{notice.texte}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {/* Deux colonnes : les listes disponibles à gauche, celles qui
          recevront le message à droite. On y dépose, ou l'on clique — le
          glisser-déposer est le geste le plus direct, il ne doit pas être le
          seul : il n'existe pas au doigt sur un téléphone. */}
      <section className="card space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Listes disponibles
            </p>
            <div className="space-y-2 min-h-[4rem]">
              {(data?.audiences ?? [])
                .filter((a) => !choisies.includes(a.code))
                .map((a) => (
                  <button
                    key={a.code}
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', a.code);
                      setGlisse(a.code);
                    }}
                    onDragEnd={() => setGlisse(null)}
                    onClick={() => ajouter(a.code)}
                    className={`w-full text-left rounded-xl border-2 p-3 cursor-grab active:cursor-grabbing transition-colors border-gray-200 dark:border-gray-700 hover:border-primary-300 ${
                      glisse === a.code ? 'opacity-40' : ''
                    }`}
                  >
                    <p className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
                      {a.consentement ? (
                        <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
                      ) : (
                        <Users className="w-4 h-4 text-primary-600 shrink-0" />
                      )}
                      {a.libelle}
                      <span className="ml-auto text-sm tabular-nums text-gray-500">
                        {a.destinataires}
                      </span>
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{a.nature}</p>
                  </button>
                ))}
              {(data?.audiences ?? []).every((a) => choisies.includes(a.code)) && (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-3">
                  Toutes les listes sont retenues.
                </p>
              )}
            </div>
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setSurvol(true);
            }}
            onDragLeave={() => setSurvol(false)}
            onDrop={(e) => {
              e.preventDefault();
              setSurvol(false);
              const code = e.dataTransfer.getData('text/plain');
              if (code) ajouter(code);
            }}
          >
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Recevront ce message
            </p>
            <div
              className={`space-y-2 min-h-[4rem] rounded-xl border-2 border-dashed p-2 transition-colors ${
                survol
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              {choisies.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-2">
                  Déposez ici les listes à qui écrire, ou cliquez-les à gauche.
                </p>
              )}
              {choisies.map((code) => {
                const a = (data?.audiences ?? []).find((x) => x.code === code);
                if (!a) return null;
                return (
                  <div
                    key={code}
                    className="rounded-xl border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/20 p-3"
                  >
                    <p className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      {a.consentement ? (
                        <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
                      ) : (
                        <Users className="w-4 h-4 text-primary-600 shrink-0" />
                      )}
                      {a.libelle}
                      <span className="ml-auto text-sm tabular-nums text-gray-500">
                        {a.destinataires}
                      </span>
                      <button
                        type="button"
                        title="Retirer cette liste"
                        onClick={() => retirer(code)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{a.nature}</p>
                  </div>
                );
              })}
            </div>

            {choisies.length > 1 && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                Les adresses présentes dans plusieurs listes ne reçoivent qu'un seul message —
                un bénévole qui est aussi adhérent est le cas ordinaire.
              </p>
            )}
          </div>
        </div>

        {vide && (
          <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
            {choisies.includes('newsletter')
              ? "Personne n'a encore accepté la lettre d'information. Chacun la coche lui-même dans ses informations personnelles — le bureau ne peut pas cocher à sa place."
              : choisies.length === 0
                ? 'Choisissez au moins une liste.'
                : 'Ces listes ne comptent aucune adresse utilisable.'}
          </p>
        )}

        {conflitNewsletter && (
          <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
            La lettre d'information s'envoie seule : elle repose sur un consentement que les autres
            listes n'ont pas demandé. Retirez-la, ou retirez les autres.
          </p>
        )}

        {/* Les modèles. Devant un champ vide on remet à plus tard, et le
            message part finalement sur un groupe de téléphones où personne ne
            retrouvera rien. Un départ vaut mieux qu'une page blanche. */}
        {(data?.modeles ?? []).length > 0 && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2">
            <button
              type="button"
              className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100"
              onClick={() => setModelesOuverts((v) => !v)}
            >
              <FileText className="w-4 h-4 text-primary-600" />
              Partir d'un modèle
              <ChevronDown
                className={`w-4 h-4 transition-transform ${modelesOuverts ? 'rotate-180' : ''}`}
              />
            </button>

            {modelesOuverts && (
              <div className="space-y-3 pt-1">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Ce sont des départs, pas des textes à envoyer tels quels : ce qui est entre
                  crochets vous revient. Choisir un modèle remplace le sujet et le message —
                  écrivez par-dessus.{' '}
                  <Link to="/bureau/diffusion/modeles" className="underline font-medium">
                    Modifier vos modèles
                  </Link>
                  .
                </p>

                {Object.entries(
                  (data!.modeles ?? []).reduce<Record<string, ModeleDiffusion[]>>((acc, m) => {
                    (acc[m.famille] ??= []).push(m);
                    return acc;
                  }, {})
                ).map(([famille, liste]) => (
                  <div key={famille}>
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                      {famille}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {liste.map((m) => (
                        <button
                          key={m.code}
                          type="button"
                          className="text-xs rounded-full border border-gray-300 dark:border-gray-600 px-3 py-1.5 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                          onClick={() => {
                            setSujet(m.sujet);
                            setCorps(m.corps);
                            // La liste proposée par le modèle, pas imposée :
                            // convoquer ses bénévoles à l'assemblée générale a
                            // ses raisons. Elle reste modifiable au-dessus, et
                            // d'autres listes peuvent s'y ajouter.
                            if (m.audience) {
                              setChoisies([m.audience]);
                            }
                            setConfirme(false);
                            setModelesOuverts(false);
                          }}
                        >
                          {m.titre}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <label className="block text-sm text-gray-600 dark:text-gray-400">
          Sujet
          <input
            className="input mt-1"
            value={sujet}
            onChange={(e) => setSujet(e.target.value)}
            placeholder="Convocation à l'assemblée générale du 14 mars"
          />
        </label>

        <label className="block text-sm text-gray-600 dark:text-gray-400">
          Message
          <textarea
            className="input mt-1 min-h-[12rem]"
            value={corps}
            onChange={(e) => setCorps(e.target.value)}
            placeholder={'Bonjour,\n\nLe bureau vous convie à…'}
          />
          <span className="text-xs text-gray-400">
            Texte simple : les paragraphes sont séparés par une ligne vide. Le nom de l'association
            est ajouté au bas du message.
          </span>
        </label>

        {/* La confirmation n'est pas un obstacle décoratif : elle affiche le
            nombre, et c'est le nombre qui fait réfléchir. */}
        <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={confirme}
            onChange={(e) => setConfirme(e.target.checked)}
            className="mt-1"
            disabled={vide}
          />
          <span>
            J'envoie ce message à <strong>{nombre} destinataire{nombre > 1 ? 's' : ''}</strong>
            {choisies.length > 1 ? ' au plus, doublons retirés' : ''}.
            {choisies.includes('newsletter')
              ? " Ils ont tous accepté de recevoir la lettre d'information."
              : ' Ils le reçoivent au titre de leur lien avec l’association, sans avoir eu à s’y abonner.'}
          </span>
        </label>

        <button
          type="button"
          onClick={() => envoyer.mutate()}
          disabled={!pret || envoyer.isPending}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {envoyer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Envoyer
        </button>
      </section>

      {(data?.derniers?.length ?? 0) > 0 && (
        <section className="card space-y-3">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Derniers envois</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Pour savoir si c'est déjà parti — c'est ainsi qu'on évite de l'envoyer deux fois.
          </p>
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {data!.derniers.map((d) => (
              <li key={d.id} className="py-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                  {d.envoyeLe ? horodatage.format(new Date(d.envoyeLe)) : '—'}
                </span>
                <span className="text-gray-900 dark:text-gray-100">{d.sujet}</span>
                <span className="text-xs text-gray-500">{d.audienceLabel}</span>
                {d.auteurNom && <span className="text-xs text-gray-400">par {d.auteurNom}</span>}
                <span className="ml-auto text-xs tabular-nums text-gray-500">
                  {d.destinataires} envoi{d.destinataires > 1 ? 's' : ''}
                  {d.echecs > 0 && <span className="text-red-600"> — {d.echecs} refusé{d.echecs > 1 ? 's' : ''}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
